import { surveyIds } from '../constants/SurveyIds';
import type { SurveyQuestion } from '../types/Survey.type';

/**
 * Single source of truth for mapping picked body parts → disability followup
 * survey IDs (NDI, Oswestry, DASH, LEFS). Used by:
 * - FE getCCSFollowups (single CCS / ACCS body-picker submission)
 * - BE computeAccsChainEndFollowups (aggregated across the ACCS chain)
 *
 * Rules (with cross-region suppressions):
 * - neck → NDI
 * - upper-back / upper-lower-back → Oswestry (suppressed if neck is also picked)
 * - lower-back → Oswestry
 * - shoulder / elbow / hand / wrist / arm → DASH (suppressed if neck is also picked)
 * - hip / knee / thigh / foot / ankle / leg → LEFS (suppressed if any back region is also picked)
 *
 * Post-filters (preserved from main's caller — historically applied in
 * `libs/components/src/lib/body/helpers.ts` after merging body-picker and
 * complaint followups, not inside the original `getCCSFollowups`. The
 * upstream suppression guards above already cover these combinations, but
 * we keep the post-filters here so this single source of truth produces an
 * identical result to main and stays safe if a future rule loosens the
 * upstream guards):
 * - Drop NDI when both NDI and DASH are present.
 * - Drop LEFS when both LEFS and Oswestry are present.
 */
export const deriveCCSFollowupsFromBodyParts = (
  bodyPartsPicked: Record<string, boolean>
): string[] => {
  const raw: string[] = [];

  for (const part of Object.keys(bodyPartsPicked)) {
    if (!bodyPartsPicked[part]) continue;

    if (part === 'neck' && !raw.includes(surveyIds.ndi)) {
      raw.push(surveyIds.ndi);
    }

    if (
      (part === 'upper-back' || part === 'upper-lower-back') &&
      !raw.includes(surveyIds.oswestry) &&
      !bodyPartsPicked.neck
    ) {
      raw.push(surveyIds.oswestry);
    }

    if (part === 'lower-back' && !raw.includes(surveyIds.oswestry)) {
      raw.push(surveyIds.oswestry);
    }

    if (
      ['shoulder', 'elbow', 'hand', 'wrist', 'arm'].includes(part) &&
      !raw.includes(surveyIds.dash) &&
      !bodyPartsPicked.neck
    ) {
      raw.push(surveyIds.dash);
    }

    if (
      ['hip', 'knee', 'thigh', 'foot', 'ankle', 'leg'].includes(part) &&
      !raw.includes(surveyIds.lefs) &&
      !bodyPartsPicked['lower-back'] &&
      !bodyPartsPicked['upper-back'] &&
      !bodyPartsPicked['upper-lower-back']
    ) {
      raw.push(surveyIds.lefs);
    }
  }

  return raw.filter(id => {
    if (id === surveyIds.ndi && raw.includes(surveyIds.ndi) && raw.includes(surveyIds.dash)) {
      return false;
    }

    return !(
      id === surveyIds.lefs &&
      raw.includes(surveyIds.oswestry) &&
      raw.includes(surveyIds.lefs)
    );
  });
};

/**
 * Aggregates a flat parts map from a list of body-picker SurveyQuestions.
 * Each question's `answer.value.parts` has shape `{ front?: {...}, rear?: {...} }`,
 * where each side is a map of part name → `{ left?: boolean, right?: boolean }`.
 *
 * The output is a flat `{ partName: true }` map — anatomically scoped, sides
 * collapsed. Suitable input for {@link deriveCCSFollowupsFromBodyParts}.
 */
export const aggregateBodyPartsFromBodyPickerQuestions = (
  bodyPickerQuestions: SurveyQuestion[]
): Record<string, boolean> => {
  const bodyPartsPicked: Record<string, boolean> = {};

  for (const q of bodyPickerQuestions) {
    const parts = q?.answer?.value?.parts;
    if (!parts) continue;

    for (const side of Object.keys(parts)) {
      const sideObj = parts[side] || {};

      for (const part of Object.keys(sideObj)) {
        if (sideObj[part]?.right || sideObj[part]?.left) {
          bodyPartsPicked[part] = true;
        }
      }
    }
  }

  return bodyPartsPicked;
};
